import type { QueuedTransaction } from './types'

/**
 * The queue's durable store. The flush engine only ever talks to this
 * interface, so the engine is testable in memory and IndexedDB stays a thin,
 * replaceable adapter.
 */
export type QueueStorage = {
  put(transaction: QueuedTransaction): Promise<void>
  /** Every transaction, any state, oldest transaction first. */
  all(): Promise<QueuedTransaction[]>
  /** Pending work whose `nextAttemptAt` has arrived, oldest transaction first. */
  due(now: number): Promise<QueuedTransaction[]>
  remove(id: string): Promise<void>
  clear(): Promise<void>
}

/** FIFO by when the operator acted, falling back to enqueue order. */
export function compareTransactions(a: QueuedTransaction, b: QueuedTransaction): number {
  const byOccurred = a.occurredAtUtc.localeCompare(b.occurredAtUtc)
  return byOccurred !== 0 ? byOccurred : a.enqueuedAt - b.enqueuedAt
}

/**
 * In-memory store: what the unit tests run against, and the safety net if a
 * browser denies IndexedDB (private mode, exhausted quota). It loses work on
 * reload, so `createQueueStorage()` only falls back to it as a last resort —
 * a working queue that forgets on reboot still beats no queue at all.
 */
export function createMemoryStorage(seed: QueuedTransaction[] = []): QueueStorage {
  const rows = new Map<string, QueuedTransaction>(seed.map(op => [op.id, op]))

  return {
    async put(transaction) {
      rows.set(transaction.id, { ...transaction })
    },
    async all() {
      return [...rows.values()].map(op => ({ ...op })).sort(compareTransactions)
    },
    async due(now) {
      return [...rows.values()]
        .filter(op => op.state === 'pending' && op.nextAttemptAt <= now)
        .map(op => ({ ...op }))
        .sort(compareTransactions)
    },
    async remove(id) {
      rows.delete(id)
    },
    async clear() {
      rows.clear()
    },
  }
}
